import React from 'react';
import { IconButton, Menu, MenuItem } from '@mui/material';
import {
  BookmarkBorderRounded,
  BookmarkRounded,
  IosShareRounded,
  MoreHorizRounded,
} from '@mui/icons-material';
import { IMenuLink } from '@/infrastructure/interfaces/i-link';
import { useTranslation } from 'next-i18next';
import { NavigatorEvents } from '@/utils/helpers/navigator-events';
import { Ayat } from '@/infrastructure/class/ayat';
import { AudioSurah } from '@/infrastructure/class/audio-surah';
import { ReadSurah } from '@/infrastructure/class/read-surah';
import { useAuth } from '@/infrastructure/context/auth-context';
import { useReadBookmarks } from '@/infrastructure/context/read-bookmarks-context';
import { useAudioBookmarks } from '@/infrastructure/context/audio-bookmarks-context';

interface IAyasListItemMenuBtnProps {
  data: Ayat;
  surah: AudioSurah | ReadSurah;
  className?: string;
}

const AyasListItemMenuBtn: React.FC<IAyasListItemMenuBtnProps> = (props) => {
  const { data, surah, className = '' } = props;
  const { audioBookmarks, changeAudioBookmark } = useAudioBookmarks();
  const { readBookmarks, changeReadBookmark, isBookmarked } =
    useReadBookmarks();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const { authUser, checkAuth, showMessage } = useAuth();

  const isAudioSurah = surah instanceof AudioSurah;
  const isReadSurah = surah instanceof ReadSurah;

  const bookmark = isAudioSurah
    ? audioBookmarks.isBookmark(surah, data.number)
    : isBookmarked(surah, data.number);

  const { t: tButtons } = useTranslation('buttons');

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const onShare = async () => {
    await NavigatorEvents.copyString(
      `${window.location.origin}${window.location.pathname}?number=${data.number}`
    );
    handleClose();
    showMessage(tButtons('linkCopied'));
  };

  const onBookmark = async () => {
    handleClose();
    await checkAuth();
    if (authUser) {
      if (isAudioSurah)
        changeAudioBookmark({
          user: authUser,
          audioSurah: surah,
          timing: data.number,
        });
      if (isReadSurah)
        changeReadBookmark({
          user: authUser,
          readSurah: surah,
          ayaNumber: data.number,
        });

      const message = bookmark
        ? tButtons('removedFromBookmarks')
        : tButtons('addedToBookmarks');
      showMessage(message);
    }
  };

  const menuItems: IMenuLink[] = [
    {
      title: tButtons('addToBookmark'),
      icon: BookmarkBorderRounded,
      action: onBookmark,
      visible: !bookmark,
    },
    {
      title: tButtons('removeFromBookmark'),
      icon: BookmarkRounded,
      action: onBookmark,
      visible: !!bookmark,
    },
    {
      title: tButtons('share'),
      icon: IosShareRounded,
      action: onShare,
      visible: true,
    },
  ];

  return (
    <div className={className}>
      <IconButton color="error" onClick={handleClick} size="small">
        <MoreHorizRounded />
      </IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {menuItems.map(
          (menuItem) =>
            menuItem.visible && (
              <MenuItem key={menuItem.title} onClick={menuItem.action}>
                {menuItem.icon && (
                  <i>
                    <menuItem.icon />
                  </i>
                )}
                {menuItem.title}
              </MenuItem>
            )
        )}
      </Menu>
    </div>
  );
};

export default AyasListItemMenuBtn;
